import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { CONTENT } from '../content';
import { getRoadmapEntries, flattenMilestones } from '../../domain/roadmap/roadmapMilestones';
import { useTokenomics } from '../useTokenomics';
import { RibbonMarquee } from '../RibbonMarquee';
import { RedesignComicViewer } from '../RedesignComicViewer';
import { RedesignNav, RedesignFooter } from '../SharedComponents';

const BrandGlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'BreakingTheComic';
    src: url('/assets/fonts/BreakingTheComic.woff2') format('woff2');
  }
  @font-face {
    font-family: 'GROBOLD';
    src: url('/assets/fonts/GROBOLD.woff2') format('woff2');
  }
  body, html {
    margin: 0;
    padding: 0;
    background-color: #3D8DF5;
    background-image: url('/assets/img/hero/bg.webp');
    background-size: cover;
    background-attachment: fixed;
    background-position: center;
    font-family: 'BreakingTheComic', sans-serif;
    color: #fff;
    overflow-x: hidden;
  }
  * { box-sizing: border-box; }
  
  /* Shared text outline for comic feel */
  .comic-text {
    text-shadow: 2px 2px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 0 2px 0 #000, 2px 0 0 #000, 0 -2px 0 #000, -2px 0 0 #000;
  }
`;

const SectionWrapper = styled.section`
  position: relative;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  overflow: hidden;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  position: relative;
  z-index: 2;
`;

const FloatingCharacter = styled(motion.img)`
  position: absolute;
  z-index: 1;
  pointer-events: auto;
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.5));
`;

const TitleImage = styled(motion.img)`
  max-width: 90%;
  height: auto;
  margin-bottom: 40px;
  display: block;
  margin-left: auto;
  margin-right: auto;
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const TextBlock = styled(motion.div)`
  background: #fff;
  color: #000;
  border: 4px solid #000;
  border-radius: 24px;
  padding: 40px;
  font-family: 'Inter', sans-serif;
  font-size: 1.2rem;
  line-height: 1.6;
  box-shadow: 8px 8px 0 #000;
  margin-bottom: 20px;
  
  p { margin-top: 0; }
  p:last-child { margin-bottom: 0; }
  
  @media (max-width: 768px) {
    padding: 20px;
    font-size: 1rem;
    box-shadow: 4px 4px 0 #000;
  }
`;

const PrimaryButton = styled(motion.a)`
  display: inline-block;
  background: #F7E03C;
  color: #000;
  font-family: 'GROBOLD', sans-serif;
  font-size: 1.5rem;
  padding: 15px 40px;
  border: 4px solid #000;
  border-radius: 50px;
  text-decoration: none;
  box-shadow: 6px 6px 0 #000;
  cursor: pointer;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: 10px 20px;
    box-shadow: 4px 4px 0 #000;
  }
`;

const TokenomicsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 40px;
`;

const TokenCard = styled(motion.a)`
  background: #fff;
  color: #000;
  border: 4px solid #000;
  border-radius: 20px;
  padding: 30px;
  text-decoration: none;
  box-shadow: 6px 6px 0 #000;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  h4 {
    font-family: 'GROBOLD', sans-serif;
    font-size: 1.5rem;
    margin: 0 0 10px 0;
  }
  p {
    font-family: 'Inter', sans-serif;
    margin: 0;
  }
`;

const RoadmapItem = styled(motion.div)`
  background: #fff;
  color: #000;
  border: 4px solid #000;
  border-radius: 20px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 6px 6px 0 #000;
  
  h3 {
    font-family: 'GROBOLD', sans-serif;
    margin: 0 0 10px 0;
    font-size: 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .status {
    font-size: 1rem;
    background: #F7E03C;
    padding: 5px 10px;
    border-radius: 12px;
    border: 2px solid #000;
  }
  p {
    font-family: 'Inter', sans-serif;
    margin: 0;
  }
`;

const MemeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 40px;
`;

const MemeImg = styled(motion.img)`
  width: 100%;
  border: 4px solid #000;
  border-radius: 12px;
  box-shadow: 4px 4px 0 #000;
`;

const CopyToast = styled(motion.div)`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: #000;
  color: #F7E03C;
  padding: 10px 20px;
  border-radius: 20px;
  font-family: 'GROBOLD', sans-serif;
  z-index: 1000;
`;

export default function RedesignC() {
  const roadmap = flattenMilestones(getRoadmapEntries());
  const { data: tokenomics } = useTokenomics();
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(CONTENT.hero.ca);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const hoverProps = {
    whileHover: { scale: 1.05, y: -5, boxShadow: "10px 10px 0 #000" },
    transition: { type: "spring" as const, stiffness: 300, damping: 20 }
  };
  
  const charHoverProps = {
    whileHover: { scale: 1.1, rotate: [-2, 2, -2, 0], y: -10 },
  };

  return (
    <>
      <BrandGlobalStyle />
      <RedesignNav />
      
      {/* HERO */}
      <SectionWrapper>
        <ContentContainer style={{ textAlign: 'center', zIndex: 10 }}>
          <motion.img 
            src="/assets/img/hero/title.webp" 
            alt="Zeus Pepes Dog" 
            style={{ maxWidth: '90%', height: 'auto', marginBottom: '20px' }}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', bounce: 0.5 }}
          />
          <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
            <PrimaryButton 
              href={CONTENT.hero.buyLink}
              target="_blank"
              {...hoverProps}
            >
              BUY ZEUS
            </PrimaryButton>
            <motion.div 
              onClick={handleCopy}
              style={{ background: '#fff', color: '#000', padding: '10px 20px', borderRadius: '20px', border: '3px solid #000', cursor: 'pointer', fontFamily: 'Inter', fontWeight: 'bold' }}
              whileHover={{ scale: 1.05, background: '#e2e8f0' }}
            >
              CA: {CONTENT.hero.ca}
            </motion.div>
          </div>
        </ContentContainer>
        <FloatingCharacter 
          src="/assets/img/join/01.webp" 
          style={{ right: '5%', top: '20%', width: '35%', maxWidth: '400px', minWidth: '150px' }}
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: [0, -20, 0], opacity: 1 }}
          transition={{ y: { repeat: Infinity, duration: 4, ease: "easeInOut" }, opacity: { duration: 1 } }}
          {...charHoverProps}
        />
        <FloatingCharacter 
          src="/assets/img/about/01.webp" 
          style={{ left: '5%', bottom: '10%', width: '30%', maxWidth: '350px', minWidth: '150px' }}
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: [0, 20, 0], opacity: 1 }}
          transition={{ y: { repeat: Infinity, duration: 5, ease: "easeInOut" }, opacity: { duration: 1 } }}
          {...charHoverProps}
        />
      </SectionWrapper>

      <RibbonMarquee />

      {/* VIDEO */}
      <SectionWrapper style={{ minHeight: 'auto', padding: '40px 20px' }}>
        <ContentContainer>
          <motion.video
            controls
            poster="/assets/img/hero/bg.webp"
            style={{ width: '100%', maxWidth: '800px', margin: '0 auto', display: 'block', border: '6px solid #000', borderRadius: '24px', boxShadow: '8px 8px 0 #000', backgroundColor: '#000' }}
            whileHover={{ scale: 1.02 }}
          >
            <source src="/assets/img/video/01.mp4" type="video/mp4" />
          </motion.video>
        </ContentContainer>
      </SectionWrapper>

      {/* ABOUT */}
      <SectionWrapper>
        <ContentContainer style={{ maxWidth: '800px', marginLeft: '0' }}>
          <TitleImage src="/assets/img/about/title-pepesdog.webp" />
          <TextBlock>
            {CONTENT.about.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </TextBlock>
        </ContentContainer>
        <FloatingCharacter 
          src="/assets/img/about-cto/01.webp" 
          style={{ right: '0%', top: '30%', width: '40%', maxWidth: '500px', minWidth: '200px' }}
          {...charHoverProps}
        />
      </SectionWrapper>

      <RibbonMarquee />

      {/* CTO */}
      <SectionWrapper>
        <FloatingCharacter 
          src="/assets/img/artist/01.webp" 
          style={{ left: '-5%', top: '20%', width: '35%', maxWidth: '450px', minWidth: '180px' }}
          {...charHoverProps}
        />
        <ContentContainer style={{ maxWidth: '800px', marginLeft: 'auto' }}>
          <TitleImage src="/assets/img/about-cto/title.webp" />
          <TextBlock>
            {CONTENT.aboutCto.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </TextBlock>
        </ContentContainer>
      </SectionWrapper>

      <RibbonMarquee />

      {/* TOKENOMICS */}
      <SectionWrapper>
        <ContentContainer>
          <TitleImage src="/assets/img/tokenomics/title.webp" />
          {tokenomics && (
            <motion.div style={{ textAlign: 'center', marginBottom: '40px' }} initial={{ scale: 0.9 }} whileInView={{ scale: 1 }}>
              <h3 className="comic-text" style={{ fontSize: '3rem', margin: 0, color: '#F7E03C' }}>{tokenomics.lpZeusPct.toFixed(1)}% LP LOCKED</h3>
              <h3 className="comic-text" style={{ fontSize: '2rem', margin: 0 }}>{tokenomics.circulatingPct.toFixed(1)}% CIRCULATING</h3>
            </motion.div>
          )}
          <TokenomicsGrid>
            {CONTENT.tokenomics.cards.map((card, i) => (
              <TokenCard key={i} href={card.link} target="_blank" {...hoverProps}>
                <h4>{card.title}</h4>
                <p>{card.text}</p>
              </TokenCard>
            ))}
          </TokenomicsGrid>
        </ContentContainer>
      </SectionWrapper>

      <RibbonMarquee />

      {/* ROADMAP */}
      <SectionWrapper>
        <ContentContainer style={{ maxWidth: '800px' }}>
          <TitleImage src="/assets/img/roadmap/title.webp" />
          <div>
            {roadmap.map((ms, i) => (
              <RoadmapItem 
                key={i}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                {...hoverProps}
              >
                <h3>{ms.title} <span className="status">{ms.status}</span></h3>
                <p>{ms.description}</p>
              </RoadmapItem>
            ))}
          </div>
        </ContentContainer>
      </SectionWrapper>

      <RibbonMarquee />

      {/* ARTIST */}
      <SectionWrapper>
        <ContentContainer style={{ maxWidth: '800px', textAlign: 'center' }}>
          <TitleImage src="/assets/img/artist/title.webp" />
          <TextBlock>
            {CONTENT.artist.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </TextBlock>
        </ContentContainer>
        <FloatingCharacter 
          src="/assets/img/tokenomics/01.webp" 
          style={{ right: '5%', bottom: '0%', width: '30%', maxWidth: '350px', minWidth: '150px' }}
          {...charHoverProps}
        />
      </SectionWrapper>

      <RibbonMarquee />

      {/* MEMES */}
      <SectionWrapper>
        <ContentContainer>
          <TitleImage src="/assets/img/memes/title.webp" />
          <MemeGrid>
            {[...Array(18)].map((_, i) => (
              <MemeImg 
                key={i} 
                src={`/assets/img/memes/${String(i + 1).padStart(2, '0')}.webp`}
                whileHover={{ scale: 1.1, zIndex: 10, rotate: Math.random() * 10 - 5 }}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
              />
            ))}
          </MemeGrid>
        </ContentContainer>
      </SectionWrapper>

      <RibbonMarquee />

      {/* COMIC */}
      <SectionWrapper>
        <ContentContainer>
          <RedesignComicViewer />
        </ContentContainer>
      </SectionWrapper>

      <RibbonMarquee />

      {/* JOIN */}
      <SectionWrapper style={{ minHeight: '60vh' }}>
        <ContentContainer style={{ textAlign: 'center' }}>
          <h2 className="comic-text" style={{ fontSize: '4rem', color: '#F7E03C', marginBottom: '40px' }}>JOIN THE ARMY</h2>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {Object.entries(CONTENT.social).map(([key, link]) => (
              <PrimaryButton key={key} href={link} target="_blank" {...hoverProps} style={{ fontSize: '1.2rem', padding: '10px 20px' }}>
                {key.toUpperCase()}
              </PrimaryButton>
            ))}
          </div>
        </ContentContainer>
        <FloatingCharacter 
          src="/assets/img/join/01.webp" 
          style={{ left: '5%', top: '20%', width: '30%', maxWidth: '350px', minWidth: '150px' }}
          {...charHoverProps}
        />
      </SectionWrapper>
      
      <RedesignFooter />

      <AnimatePresence>
        {copied && (
          <CopyToast
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
          >
            COPIED TO CLIPBOARD!
          </CopyToast>
        )}
      </AnimatePresence>
    </>
  );
}
